// Container principal da página Atividades. Cuida de:
// - Carregamento/refresh da lista (com filtros aplicados via query)
// - Toolbar de visualização (lista / kanban / calendário)
// - Filtros de escopo, módulo, status, prazo, prioridade, atribuído, ordenação
// - Bulk actions (concluir/atribuir/excluir) na view lista
// - Deep-link via notificação (sessionStorage 'atividades_abrir_id')
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Input, Select, Tag, Modal, Typography, Space,
  Radio, App as AntApp, Flex,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, FireOutlined, CalendarOutlined,
  AppstoreOutlined, UnorderedListOutlined,
  DeleteOutlined, CheckCircleOutlined, UserAddOutlined, CloseOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';
import { AdminHierarquiaInner } from '../AdminHierarquia';
import ListaAtividades from './ListaAtividades';
import KanbanAtividades from './KanbanAtividades';
import CalendarioAtividades from './CalendarioAtividades';
import ModalAtividade from './ModalAtividade';
import { STATUS_LABEL } from './types';
import type { Atividade, AtividadesProps } from './types';

const { Title, Text } = Typography;

export default function AtividadesInner({ moduloFixo, origemModulo, origemId, origemLabel, onIrParaOrigem }: AtividadesProps) {
  const { message, modal } = AntApp.useApp();
  const [view, setView] = useState<'lista' | 'kanban' | 'calendario'>('lista');
  const [escopo, setEscopo] = useState<'minhas' | 'criadas' | 'equipe' | 'todas'>('minhas');
  // Filtro de status agora é multi-select. Default exclui concluída/cancelada
  // pra usuário focar no que está pendente. Persiste em sessionStorage pra
  // não resetar a cada navegação.
  const [filtroStatus, setFiltroStatus] = useState<string[]>(() => {
    const raw = sessionStorage.getItem('atividades_filtro_status');
    if (raw) { try { return JSON.parse(raw); } catch {} }
    return ['pendente', 'em_andamento'];
  });
  useEffect(() => {
    sessionStorage.setItem('atividades_filtro_status', JSON.stringify(filtroStatus));
  }, [filtroStatus]);
  const [filtroModulo, setFiltroModulo] = useState<string>(moduloFixo || '');
  const [busca, setBusca] = useState('');
  const [filtroPrazo, setFiltroPrazo] = useState<string>('');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('');
  const [filtroAtribuido, setFiltroAtribuido] = useState<string>('');
  const [ordenacao, setOrdenacao] = useState<string>('');
  const [usuariosListagem, setUsuariosListagem] = useState<{ email: string; nome?: string }[]>([]);
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState<Atividade | null>(null);
  const [novo, setNovo] = useState(false);
  const [meuEmail, setMeuEmail] = useState('');
  // Onda F5: souAdmin via matriz nova.
  const { pode } = usePerms();
  const souAdmin = pode('admin.permissoes.editar');
  const [temSubordinados, setTemSubordinados] = useState(false);
  const [mostrarGerenciarEquipe, setMostrarGerenciarEquipe] = useState(false);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [bulkAcao, setBulkAcao] = useState<'concluir' | 'atribuir' | null>(null);
  const [bulkEmailAtribuir, setBulkEmailAtribuir] = useState('');

  useEffect(() => {
    (async () => {
      const session = await fetchAuthSession();
      const payload: any = session.tokens?.idToken?.payload || {};
      const email = (payload.email || '').toLowerCase();
      setMeuEmail(email);
      // F5: souAdmin agora vem do hook usePerms() (matriz nova).
      // Cognito groups nao usado mais aqui.
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/atividades`,
          { action: 'hierarquia_meus_subordinados' },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setTemSubordinados((d.subordinados || []).length > 0);
      } catch {}
    })();
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (origemModulo && origemId) {
        params.set('escopo', 'origem');
        params.set('origem_modulo', origemModulo);
        params.set('origem_id', origemId);
      } else {
        params.set('escopo', escopo);
        if (filtroModulo) params.set('modulo', filtroModulo);
      }
      // Multi-select: envia CSV pro backend (status=pendente,em_andamento)
      if (filtroStatus.length > 0) params.set('status', filtroStatus.join(','));
      if (busca) params.set('busca', busca);
      if (filtroPrazo) params.set('prazo', filtroPrazo);
      if (filtroPrioridade) params.set('prioridade', filtroPrioridade);
      if (filtroAtribuido) params.set('atribuido_a', filtroAtribuido);
      if (ordenacao) params.set('ordenacao', ordenacao);
      const r = await axios.get(`${API_URL}/atividades?${params}`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      setAtividades(Array.isArray(data) ? data : []);
    } catch (e: any) {
      console.error('Erro carregar atividades:', e);
      setAtividades([]);
    } finally { setLoading(false); }
  }, [escopo, filtroStatus, filtroModulo, origemModulo, origemId,
      busca, filtroPrazo, filtroPrioridade, filtroAtribuido, ordenacao]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuariosListagem(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Deep-link via notificação: se App.tsx gravou 'atividades_abrir_id' no
  // sessionStorage (clique em notificação que aponta pra uma atividade),
  // abre o modal automaticamente quando a lista terminar de carregar.
  useEffect(() => {
    if (loading || atividades.length === 0) return;
    const id = sessionStorage.getItem('atividades_abrir_id');
    if (!id) return;
    sessionStorage.removeItem('atividades_abrir_id');
    const ativ = atividades.find(a => a.id === id);
    if (ativ) setEditando(ativ);
  }, [loading, atividades]);

  const isEmbed = !!moduloFixo || !!origemId;

  async function alterarStatus(id: string, novoStatus: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'update', id, status: novoStatus },
        { headers: { Authorization: token } });
      carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  async function bulkAlterarStatus(novoStatus: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'bulk_status', ids: selecionadas, status: novoStatus },
        { headers: { Authorization: token } });
      message.success(`${selecionadas.length} atividade(s) atualizada(s).`);
      setSelecionadas([]); setBulkAcao(null); carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  async function bulkAtribuir() {
    if (!bulkEmailAtribuir.trim()) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'bulk_atribuir', ids: selecionadas,
          emails: [bulkEmailAtribuir.trim().toLowerCase()], modo: 'adicionar' },
        { headers: { Authorization: token } });
      message.success(`Atribuído a ${selecionadas.length} atividade(s).`);
      setSelecionadas([]); setBulkAcao(null); setBulkEmailAtribuir(''); carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.erro || e.message));
    }
  }

  function bulkExcluir() {
    modal.confirm({
      title: `Excluir ${selecionadas.length} atividade(s)?`,
      content: 'Esta ação não pode ser desfeita. Você só pode excluir atividades que você criou (admin pode excluir qualquer).',
      okText: 'Excluir', cancelText: 'Voltar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/atividades`,
            { action: 'bulk_delete', ids: selecionadas },
            { headers: { Authorization: token } });
          const d = r.data.body ? JSON.parse(r.data.body) : r.data;
          if (d.excluidas < d.solicitadas) {
            message.warning(`${d.excluidas} de ${d.solicitadas} excluída(s). As demais não foram criadas por você.`);
          } else {
            message.success(`${d.excluidas} atividade(s) excluída(s).`);
          }
          setSelecionadas([]); setBulkAcao(null); carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.erro || e.message));
        }
      },
    });
  }

  return (
    <div style={isEmbed ? { padding: 0 } : { maxWidth: 1400, margin: '16px auto', padding: '0 24px' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={10} style={{ marginBottom: isEmbed ? 10 : 16 }}>
        {!isEmbed && <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Atividades</Title>}
        <Space size={8} style={{ marginLeft: isEmbed ? 'auto' : undefined }}>
          {souAdmin && !isEmbed && (
            <Button size="middle" icon={<TeamOutlined />}
              onClick={() => setMostrarGerenciarEquipe(true)}
              title="Cadastrar quem reporta a quem (alimenta o filtro 'Da equipe')">
              Gerenciar equipe
            </Button>
          )}
        <Button type="primary" size={isEmbed ? 'small' : 'middle'}
          icon={<PlusOutlined />} onClick={() => setNovo(true)}>
          Nova atividade
        </Button>
        </Space>
      </Flex>

      {/* Modal de gerenciamento de equipe (vínculos superior↔subordinado).
          Antes vivia em Admin > Hierarquia; movido pra cá porque é o único
          lugar que consome esses vínculos (filtro "Da equipe"). */}
      <Modal
        title={<span><TeamOutlined /> Gerenciar equipe — hierarquia de subordinados</span>}
        open={mostrarGerenciarEquipe}
        onCancel={() => setMostrarGerenciarEquipe(false)}
        footer={null}
        width={900}
        destroyOnClose>
        <AdminHierarquiaInner />
      </Modal>

      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 14 }} bodyStyle={{ padding: 10 }}>
        <Flex gap={12} align="center" wrap="wrap">
          <Radio.Group value={view} onChange={(e) => setView(e.target.value)} size="small">
            <Radio.Button value="lista"><UnorderedListOutlined /> Lista</Radio.Button>
            <Radio.Button value="kanban"><AppstoreOutlined /> Kanban</Radio.Button>
            <Radio.Button value="calendario"><CalendarOutlined /> Calendário</Radio.Button>
          </Radio.Group>

          {!origemId && (
            <Space size={4}>
              <Text type="secondary" style={{ fontSize: 11 }}>Escopo:</Text>
              <Select value={escopo} onChange={setEscopo} size="small" style={{ minWidth: 200 }}
                options={[
                  { label: 'Minhas (atribuídas ou criadas por mim)', value: 'minhas' },
                  { label: 'Apenas criadas por mim', value: 'criadas' },
                  ...(temSubordinados ? [{ label: '👥 Da equipe', value: 'equipe' }] : []),
                ]} />
            </Space>
          )}

          {!moduloFixo && !origemId && (() => {
            // F5 (Onda): filtra modulos pelo que o usuario pode ver na matriz.
            // Antes usava Cognito groups; agora usa perms granulares por modulo.
            const ehAdmin = souAdmin;
            const podeCom = ehAdmin || pode('comercial.dashboard.ver') || pode('comercial.pipeline.ver');
            const podeOp  = ehAdmin || pode('operacional.projetos.listar') || pode('operacional.painel_geral.ver');
            const podeAdm = ehAdmin || pode('administrativo.materiais.ver') || pode('administrativo.patrimonio.ver');
            const podeFin = ehAdmin || pode('financeiro.dre.ver') || pode('financeiro.contas_receber.ver') || pode('financeiro.contas_pagar.ver');
            const opts = [
              ...(podeCom ? [{ label: '🤝 Comercial', value: 'comercial' }] : []),
              ...(podeOp  ? [{ label: '⚙️ Operacional', value: 'operacional' }] : []),
              ...(podeAdm ? [{ label: '📦 Administrativo', value: 'administrativo' }] : []),
              ...(podeFin ? [{ label: '📊 Financeiro', value: 'financeiro' }] : []),
              { label: '👤 Pessoais', value: 'pessoais' },  // todo autenticado
            ];
            return (
              <Space size={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>Módulo:</Text>
                <Select value={filtroModulo || undefined} onChange={(v) => setFiltroModulo(v || '')}
                  placeholder="Todos" allowClear size="small" style={{ width: 160 }}
                  options={opts} />
              </Space>
            );
          })()}

          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Status:</Text>
            <Select
              mode="multiple"
              value={filtroStatus}
              onChange={setFiltroStatus}
              placeholder="Todos"
              allowClear
              size="small"
              style={{ minWidth: 200, maxWidth: 360 }}
              maxTagCount="responsive"
              options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ label: v.label, value: k }))} />
          </Space>

          <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
            {atividades.length} atividade(s)
          </Text>
        </Flex>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Flex gap={8} align="center" wrap="wrap">
          <Input value={busca} onChange={e => setBusca(e.target.value)}
            placeholder="Buscar título / descrição / origem…"
            prefix={<SearchOutlined style={{ color: '#aaa' }} />}
            size="small" allowClear style={{ minWidth: 240, flex: 1 }} />

          <Space size={4} wrap>
            {[
              { id: '',          label: 'Todos prazos' },
              { id: 'atrasadas', label: 'Atrasadas' },
              { id: 'hoje',      label: 'Hoje' },
              { id: 'semana',    label: 'Esta semana' },
              { id: 'proximos7', label: 'Próx. 7d' },
              { id: 'sem_data',  label: 'Sem prazo' },
            ].map(b => (
              <Tag.CheckableTag key={b.id}
                checked={filtroPrazo === b.id}
                onChange={() => setFiltroPrazo(b.id)}
                style={filtroPrazo === b.id && b.id === 'atrasadas'
                  ? { background: '#B91626', color: '#fff' } : undefined}
              >
                {b.id === 'atrasadas' && <FireOutlined />} {b.label}
              </Tag.CheckableTag>
            ))}
          </Space>

          <Select value={filtroPrioridade || undefined} onChange={(v) => setFiltroPrioridade(v || '')}
            placeholder="Todas prioridades" allowClear size="small" style={{ width: 150 }}
            options={[
              { label: '🔴 Urgente', value: 'urgente' },
              { label: '🟠 Alta', value: 'alta' },
              { label: '🔵 Média', value: 'media' },
              { label: '⚪ Baixa', value: 'baixa' },
            ]} />

          {(temSubordinados || escopo === 'todas') && usuariosListagem.length > 0 && (
            <Select value={filtroAtribuido || undefined} onChange={(v) => setFiltroAtribuido(v || '')}
              placeholder="Atribuído a" allowClear size="small" style={{ width: 180 }}
              showSearch optionFilterProp="label"
              options={usuariosListagem.map(u => ({ label: u.nome || u.email.split('@')[0], value: u.email }))} />
          )}

          <Select value={ordenacao || undefined} onChange={(v) => setOrdenacao(v || '')}
            placeholder="Ordem padrão" allowClear size="small" style={{ width: 160 }}
            options={[
              { label: '↑ Por prazo', value: 'prazo' },
              { label: '↑ Por prioridade', value: 'prioridade' },
              { label: '↓ Mais recentes', value: 'criacao' },
              { label: '↓ Última atualização', value: 'atualizacao' },
            ]} />

          {(busca || filtroPrazo || filtroPrioridade || filtroAtribuido || ordenacao) && (
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => {
              setBusca(''); setFiltroPrazo(''); setFiltroPrioridade('');
              setFiltroAtribuido(''); setOrdenacao('');
            }}>Limpar</Button>
          )}
        </Flex>
      </Card>

      {selecionadas.length > 0 && view === 'lista' && (
        <Card size="small" style={{ background: '#020D7F', marginBottom: 10 }} bodyStyle={{ padding: '10px 14px' }}>
          <Flex align="center" gap={10} wrap="wrap">
            <Text strong style={{ color: '#fff' }}>{selecionadas.length} selecionada(s)</Text>
            {bulkAcao === null && (
              <>
                <Button size="small" icon={<CheckCircleOutlined />} onClick={() => setBulkAcao('concluir')}>Concluir</Button>
                <Button size="small" icon={<UserAddOutlined />} onClick={() => setBulkAcao('atribuir')}>Atribuir</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={bulkExcluir}>Excluir</Button>
                <Button size="small" type="text" style={{ color: '#fff', marginLeft: 'auto' }}
                  onClick={() => setSelecionadas([])}>Limpar seleção</Button>
              </>
            )}
            {bulkAcao === 'concluir' && (
              <>
                <Text style={{ color: '#fff' }}>Confirmar concluir {selecionadas.length}?</Text>
                <Button size="small" type="primary" style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
                  onClick={() => bulkAlterarStatus('concluida')}>Sim, concluir</Button>
                <Button size="small" onClick={() => setBulkAcao(null)}>Cancelar</Button>
              </>
            )}
            {bulkAcao === 'atribuir' && (
              <>
                <Input value={bulkEmailAtribuir} onChange={e => setBulkEmailAtribuir(e.target.value)}
                  placeholder="email@hydrodataeng.com.br" size="small" style={{ width: 240 }} />
                <Button size="small" type="primary" style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
                  onClick={bulkAtribuir}>Atribuir</Button>
                <Button size="small" onClick={() => { setBulkAcao(null); setBulkEmailAtribuir(''); }}>Cancelar</Button>
              </>
            )}
          </Flex>
        </Card>
      )}

      {origemId && origemLabel && (
        <Card size="small" style={{ marginBottom: 10, background: '#FAFBFF' }} bodyStyle={{ padding: 8 }}>
          <Text style={{ fontSize: 11, color: '#020D7F' }}>
            📍 Mostrando atividades vinculadas a: <strong>{origemLabel}</strong>
          </Text>
        </Card>
      )}

      {view === 'lista' ? (
        <ListaAtividades atividades={atividades} loading={loading} meuEmail={meuEmail}
          onEditar={setEditando} onAlterarStatus={alterarStatus}
          onIrParaOrigem={onIrParaOrigem}
          selecionadas={selecionadas} setSelecionadas={setSelecionadas} />
      ) : view === 'kanban' ? (
        <KanbanAtividades atividades={atividades} loading={loading}
          onEditar={setEditando} onMudou={carregar} />
      ) : (
        <CalendarioAtividades atividades={atividades} onEditar={setEditando} />
      )}

      {(editando || novo) && (
        <ModalAtividade
          atividade={editando}
          meuEmail={meuEmail}
          origemPreset={novo && origemModulo ? {
            origem_modulo: origemModulo,
            origem_tipo: 'auto',
            origem_id: origemId,
            origem_label: origemLabel,
          } : novo && moduloFixo ? {
            origem_modulo: moduloFixo === 'pessoal' ? '' : moduloFixo,
            origem_tipo: 'modulo',
          } : undefined}
          onClose={() => { setEditando(null); setNovo(false); }}
          onSalvo={() => { setEditando(null); setNovo(false); carregar(); }}
        />
      )}
    </div>
  );
}
